/**
 * GA4 funnel tracking, via the dataLayer.
 *
 * The site already loads Google Tag Manager (GTM-W76588KT) in public/index.html,
 * which creates window.dataLayer. Every funnel event flows through the one push
 * below so GTM can turn it into a GA4 event (and, for a purchase, a Google Ads
 * conversion). Nothing here talks to GA4 or Ads directly: GTM is the single hub.
 *
 * This function must NEVER throw. Analytics failing is acceptable; a booking
 * failing because a tracking call blew up is not. Every path is guarded.
 *
 * GTM side (Phase 2): one GA4 Event tag per event name, each triggered by a
 * Custom Event trigger matching the name. Mark generate_lead and purchase as
 * key events. The events, in funnel order:
 *
 *   view_route         a route or city fare page was seen
 *   begin_checkout     rider tapped Book on a fare
 *   add_contact_info   name and mobile entered
 *   otp_sent           an OTP was requested   (watch for abuse + drop-off)
 *   otp_verified       the OTP was confirmed
 *   generate_lead      booking submitted, a lead exists      (KEY EVENT)
 *   purchase           advance paid, booking confirmed        (KEY EVENT)
 *                      carries value + currency + transaction_id
 */

/** Low-level push. Safe to call anywhere, on any render, server or browser. */
export function track(event, params = {}) {
  try {
    if (typeof window === "undefined") return
    window.dataLayer = window.dataLayer || []
    // Reset ecommerce first so values never leak between events (GA4 guidance).
    if (params && params.ecommerce) {
      window.dataLayer.push({ ecommerce: null })
    }
    window.dataLayer.push({ event, ...params })
  } catch (e) {
    // best-effort only; never let a tracking error touch the booking flow
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[analytics] push failed (ignored):", e)
    }
  }
}

// Named wrappers so call sites read like the funnel, not like plumbing.
export const trackViewRoute = (p) => track("view_route", p)
export const trackBeginCheckout = (p) => track("begin_checkout", p)
export const trackAddContactInfo = (p) => track("add_contact_info", p)
export const trackOtpSent = (p) => track("otp_sent", p)
export const trackOtpVerified = (p) => track("otp_verified", p)
export const trackGenerateLead = (p) => track("generate_lead", p)

/**
 * Fire the purchase (booking confirmed) event.
 * Pass the real booked value only, never a quote or estimate.
 * value + currency + transactionId are what GA4 and Ads de-dupe and value on.
 */
export function trackPurchase({ value, currency = "INR", transactionId, route, cabType } = {}) {
  const amount = Number(value)
  track("purchase", {
    ecommerce: {
      transaction_id: transactionId ? String(transactionId) : undefined,
      value: Number.isFinite(amount) && amount > 0 ? amount : undefined,
      currency,
      items: [
        {
          item_name: route || "cab booking",
          item_category: cabType || "outstation",
          price: Number.isFinite(amount) && amount > 0 ? amount : undefined,
          quantity: 1,
        },
      ],
    },
  })
}

const analytics = {
  track,
  trackViewRoute,
  trackBeginCheckout,
  trackAddContactInfo,
  trackOtpSent,
  trackOtpVerified,
  trackGenerateLead,
  trackPurchase,
}

export default analytics
